import {
  type ChangeEvent,
  type PropsWithChildren,
  useCallback,
  useRef,
  useState,
} from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import toast from 'react-hot-toast'
import { useParams } from 'react-router'

import { FvButton, Icon, TextAreaField } from '@fv/client-components'
import {
  defaultOptions,
  fetchJson,
  getTrackingNumLabel,
  recaptchaKey,
} from '@fv/client-core'
import { fvDate } from '@fv/dates'

import { DividedContent } from '@/components/DividedContent'
import { FooterBar } from '@/components/FooterBar'
import InfoBox from '@/components/InfoBox'
import { Pinline } from '@/components/Pinline'
import { ShipperSlider } from '@/components/ShipperSlider'
import { useBolLinkState } from '@/hooks/shipments/useBolLinkState'

import DocumentList from '../documents/DocumentList'
import { composeLabelsUrl, getParcelLabelUrl } from '../labels/hooks'
import { ShipmentLabels } from '../labels/ShipmentLabels'
import { SharedShipmentTrackingBar } from '../track/ShipmentTrackingBar'
import { useShareShipmentForm } from './mutations'
import { useSharedShipment } from './queries'
import SharedShipmentCard from './SharedShipmentCard'
import SharedShipmentHeader from './SharedShipmentHeader'
import ShareForm from './ShareForm'
import SharePanelHeader from './SharePanelHeader'
import {
  addShareTokenToDocumentUrl,
  getCarrierTrackingUrl,
  getShareToken,
} from './share-utils'

type ActivePanel = 'share' | 'labels' | null
type RequestDocsFn = (url: string) => Promise<void>

type DownloadLinkOpts = PropsWithChildren<{
  url: string
  requestDocs: RequestDocsFn
  loading?: boolean
}>

const ITEM_KEY = 'fv.pat'

const promptMessage =
  'In order to receive the requested documents, enter the passcode included in the initial share email you received from Freightview.'

function isTokenExpired() {
  const token = localStorage.getItem(ITEM_KEY)

  if (!token) return true

  try {
    const expiry = JSON.parse(atob(token.split('.')[1])).exp
    return Math.floor(new Date().getTime() / 1000) >= expiry
  } catch {
    return true
  }
}

// `!ml-0` overrides the global `.btn + .btn` sibling margin that would indent every row but the first.
const actionLinkClass = 'p-3 pl-0 !ml-0 block w-full text-left'

const DownloadLink = ({
  url,
  requestDocs,
  loading,
  children,
}: DownloadLinkOpts) => {
  return (
    <FvButton
      icon="file-contract"
      onClick={() => requestDocs(url)}
      className={actionLinkClass}
      loading={loading}
    >
      {children}
    </FvButton>
  )
}

export const SharedShipmentPage = () => {
  const { loadId } = useParams() as { loadId: string }
  const shareToken = getShareToken(window.location.search)
  const shipmentQuery = useSharedShipment(loadId, shareToken)
  const [activePanel, setActivePanel] = useState<ActivePanel>(null)
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const closePanel = useCallback(() => setActivePanel(null), [])
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [currentToken, setCurrentToken] = useState<string>()
  const hasValidShareToken = shipmentQuery.data?.hasValidShareToken ?? false

  const requestDocs = useCallback(
    async (url: string) => {
      if (loading) return

      if (hasValidShareToken && shareToken) {
        window.open(
          addShareTokenToDocumentUrl(url, shareToken),
          '_blank',
          'noopener,noreferrer',
        )
        return
      }

      let token = currentToken

      if (!token && isTokenExpired()) {
        token = window.prompt(promptMessage)
      }

      if (!token && isTokenExpired()) return

      setLoading(true)
      const recaptchaToken =
        recaptchaRef.current && (await recaptchaRef.current.executeAsync())

      const response = await fetchJson(
        url,
        defaultOptions({
          json: {
            token,
            recaptchaToken,
          },
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem(ITEM_KEY)}`,
          },
        }),
      )

      if (response.ok) {
        token && setCurrentToken(token)

        toast.success(
          'Success! The requested documents should arrive in your email in a few moments.',
          {
            duration: 10000,
            position: 'top-center',
          },
        )

        localStorage.setItem(ITEM_KEY, response.response.headers.get('X-Token'))
      } else if (response.status === 404) {
        toast.error('Invalid passcode provided. Please try again.', {
          duration: 5000,
          position: 'top-center',
        })
      } else {
        toast.error(
          'We are unable to generate the requested documents at this time. Please try again later or contact Freightview Support for assistance.',
          {
            duration: 5000,
            position: 'top-center',
          },
        )
      }

      setLoading(false)
    },
    [currentToken, hasValidShareToken, loading, shareToken],
  )

  const shareForm = useShareShipmentForm({
    loadId,
    shareToken,
  })

  const { fvBolUrl, carrierBolUrl, hideFvBolFromSpotQuote } = useBolLinkState(
    shipmentQuery.data,
  )

  const shareShipment = (emails: string[]) => {
    shareForm.submitShare(emails, {
      onSuccess: closePanel,
    })
  }

  if (shipmentQuery.isLoading) {
    return (
      <div className="flex flex-col h-screen">
        <SharedShipmentHeader>Loading shipment...</SharedShipmentHeader>
        <div className="flex-1 flex items-center justify-center">
          <Icon icon="sync" size="2x" />
        </div>
      </div>
    )
  }

  if (shipmentQuery.isError || !shipmentQuery.data) {
    return (
      <div className="flex flex-col h-screen">
        <SharedShipmentHeader>Freightview</SharedShipmentHeader>
        <div className="flex-1 flex items-center justify-center">
          <InfoBox icon="triangle">
            {shipmentQuery.error?.message ?? 'Sorry, something went wrong.'}
          </InfoBox>
        </div>
      </div>
    )
  }

  const {
    bol,
    canPrintLabels,
    carrierPhone,
    carrierTracking,
    company,
    documents,
    pickup,
    refNums,
    tracking,
    status,
    workflow,
  } = shipmentQuery.data

  const {
    currentStatus,
    deliveryDateActual,
    deliveryDateEstimate,
    trackingNumber,
  } = tracking ?? {}

  const deliveryDate = deliveryDateActual ?? deliveryDateEstimate
  const labelUrl =
    workflow === 'parcel' ? getParcelLabelUrl({ documents }) : undefined
  const carrierTrackingUrl =
    carrierTracking && hasValidShareToken && shareToken
      ? getCarrierTrackingUrl(loadId, shareToken)
      : undefined

  return (
    <div className="App">
      <div className="grid grid-cols-[19rem_1fr_30.5rem] grid-rows-[auto_1fr_auto] h-screen overflow-hidden b1000:grid-cols-1 b1000:grid-rows-none b1000:h-auto b1000:min-h-screen b1000:overflow-visible">
        <SharedShipmentHeader>
          {company} has shared this shipment with you.
        </SharedShipmentHeader>

        <div className="p-6 border-fv-gray border-r b1000:border-r-0 b1000:border-b b1000:p-4">
          <div className="divide-y divide-fv-gray">
            {fvBolUrl && !hideFvBolFromSpotQuote && (
              <DownloadLink
                url={fvBolUrl}
                requestDocs={requestDocs}
                loading={loading}
              >
                Print Freightview BOL
              </DownloadLink>
            )}

            {carrierBolUrl && (
              <DownloadLink
                url={carrierBolUrl}
                requestDocs={requestDocs}
                loading={loading}
              >
                Print Carrier BOL
              </DownloadLink>
            )}

            {canPrintLabels &&
              (labelUrl ? (
                <DownloadLink
                  url={labelUrl}
                  requestDocs={requestDocs}
                  loading={loading}
                >
                  Print Labels
                </DownloadLink>
              ) : (
                <FvButton
                  icon="tags"
                  onClick={() => setActivePanel('labels')}
                  className={actionLinkClass}
                  loading={loading}
                >
                  Print Labels
                </FvButton>
              ))}

            {hasValidShareToken && (
              <FvButton
                icon="share"
                onClick={() => setActivePanel('share')}
                className={actionLinkClass}
              >
                Share this shipment
              </FvButton>
            )}
          </div>
        </div>

        <div className="p-6 bg-[#fafafa] overflow-auto b1000:overflow-visible b1000:p-4">
          {trackingNumber && deliveryDate && (
            <>
              <p className="mb-0">
                {getTrackingNumLabel(
                  shipmentQuery.data.workflow,
                  shipmentQuery.data.selectedQuote,
                )}{' '}
                {carrierTrackingUrl ? (
                  <a href={carrierTrackingUrl} rel="noreferrer" target="_blank">
                    {trackingNumber}
                  </a>
                ) : (
                  trackingNumber
                )}{' '}
                {currentStatus === 'delivered'
                  ? 'was delivered on '
                  : 'is estimated to deliver on '}
                {fvDate(deliveryDate).format('dddd, MMM D')}
              </p>
              <Pinline rule />
            </>
          )}

          {shipmentQuery.data && (
            <SharedShipmentTrackingBar shipment={shipmentQuery.data} />
          )}
          <SharedShipmentCard shipment={shipmentQuery.data} />
        </div>

        <div className="p-6 border-fv-gray border-l overflow-auto b1000:border-l-0 b1000:border-t b1000:overflow-visible b1000:p-4">
          {status === 'canceled' && (
            <InfoBox>This shipment has been canceled.</InfoBox>
          )}

          <p className="mb-0 !leading-[1.6rem]">
            {refNums?.length > 0 &&
              `Ref# ${refNums.map(n => n.value).join(' / ')}`}
            {refNums?.length > 0 && <br />}
            {pickup?.confirmationNumber &&
              `Pickup Confirmation# ${pickup.confirmationNumber}`}
            {pickup?.confirmationNumber && <br />}
            {bol?.bolNumber && `BOL# ${bol.bolNumber}`}
          </p>
          <div className="shipment-list-documents-ctn">
            <p className="mb-0">Documents for this shipment</p>
          </div>

          <Pinline className="my-2" />
          <DocumentList
            documents={documents}
            onClick={doc => requestDocs(doc.url)}
          />
        </div>

        <FooterBar jc className="col-span-full">
          <DividedContent className="flex !justify-center b600:flex-col b600:items-center b600:gap-y-2 b600:divide-x-0 b600:[&>*]:px-0">
            {carrierTrackingUrl && (
              <a href={carrierTrackingUrl} rel="noreferrer" target="_blank">
                <Icon icon="external-link-alt" />
                <span>View carrier tracking</span>
              </a>
            )}

            {carrierPhone && (
              <a href={`tel:${carrierPhone}`}>
                <Icon icon="phone" />
                <span>Contact carrier at {carrierPhone}</span>
              </a>
            )}
          </DividedContent>
        </FooterBar>
      </div>

      <ShipperSlider isOpen={activePanel === 'share'} onClose={closePanel}>
        <SharePanelHeader
          className="border-b border-fv-gray mb-6"
          closePanel={closePanel}
        />

        <TextAreaField
          className="form-control mb-4"
          name="message"
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
            setMessage(e.target.value)
          }}
          placeholder="Enter optional message here"
          readOnly={shareForm.isSaving}
          value={message}
        />

        <ShareForm form={shareForm} shareShipment={shareShipment} />
      </ShipperSlider>

      <ShipperSlider onClose={closePanel} isOpen={activePanel === 'labels'}>
        <ShipmentLabels
          load={shipmentQuery.data}
          setActiveSlider={closePanel}
          onSubmit={labelOpts => {
            requestDocs(composeLabelsUrl({ ...labelOpts, isPublic: true }))
            closePanel()
          }}
        />
      </ShipperSlider>
      {!hasValidShareToken && recaptchaKey && (
        <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={recaptchaKey} />
      )}
    </div>
  )
}
